import React, { useEffect } from "react";
import { Stack, Alert, InputAdornment } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";

import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { resetUserPassword } from "../redux/actions/authActions";
import { PasswordResetRequestDto } from "../shared/dtos/auth/PasswordResetRequestDto";

interface ResetPasswordFormProps {
  token: string;
}

interface ResetPasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

const resetPasswordSchema: Yup.ObjectSchema<ResetPasswordFormValues> =
  Yup.object().shape({
    newPassword: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Passwords must match")
      .required("Required"),
  });

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ token }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { loadingResetPassword, errorResetPassword, serverStatus } =
    useAppSelector((state) => state.auth);

  useEffect(() => {
    if (
      serverStatus?.operation === "reset-password" &&
      serverStatus?.status === 200
    ) {
      const timer = setTimeout(() => navigate("/login"), 3000);
      return () => clearTimeout(timer);
    }
  }, [serverStatus, navigate]);

  const initialValues: ResetPasswordFormValues = {
    newPassword: "",
    confirmPassword: "",
  };

  const handleSubmit = (values: ResetPasswordFormValues): void => {
    const payload: PasswordResetRequestDto = {
      token,
      newPassword: values.newPassword.trim(),
    };
    dispatch(resetUserPassword(payload));
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={resetPasswordSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid }: FormikProps<ResetPasswordFormValues>) => (
        <Form>
          <Stack spacing={2}>
            {errorResetPassword && (
              <Alert severity="error">{errorResetPassword}</Alert>
            )}

            {serverStatus?.operation === "reset-password" && (
              <Alert severity="success">
                Password updated! Redirecting to login...
              </Alert>
            )}

            <FormTextField
              name="newPassword"
              label="New Password"
              type="password"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <FormTextField
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <SubmitButton
              loading={loadingResetPassword}
              title="Update Password"
              disabled={!isValid || serverStatus?.status === 200}
              fullWidth
              sx={{ py: 1.25 }}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default ResetPasswordForm;
